/**
 * All AI Agent Actions Registry
 * 
 * Complete list of 250 AI agent actions organized by category.
 * Used by the "Show More Options" feature to display all available actions.
 * 
 * Reference: /docs/AI_AGENT_ACTIONS.md
 */
import {
  BarChart3,
  Bell,
  Calendar,
  CalendarCheck,
  CalendarClock,
  CalendarPlus,
  Clock,
  CreditCard,
  DollarSign,
  Download,
  FileText,
  Flag,
  Folder,
  Globe,
  Hourglass,
  Image,
  LayoutDashboard,
  ListChecks,
  Mail,
  MapPin,
  Megaphone,
  Package,
  PenLine,
  Play,
  Receipt,
  RefreshCw,
  Search,
  Settings,
  ShieldCheck,
  Trash2,
  Trophy,
  User,
  UserCheck,
  UserCog,
  UserMinus,
  UserPlus,
  Users,
  type LucideIcon,
} from "lucide-react";
import type { QuickAction } from "./QuickActionCard";

export interface ActionCategory {
  id: string;
  name: string;
  icon: LucideIcon;
  actions: QuickAction[];
}

/**
 * All AI Agent Actions organized by category
 */
export const ALL_ACTION_CATEGORIES: ActionCategory[] = [
  // ============================================
  // CUSTOMER MANAGEMENT
  // ============================================
  {
    id: "customer",
    name: "Customer Management",
    icon: Users,
    actions: [
      {
        id: "search_customers",
        title: "Search Customers",
        description: "Search by name, email, or phone",
        icon: Search,
        prompt: "search_customers: Search for a customer",
      },
      {
        id: "get_customer_details",
        title: "Customer Details",
        description: "Get detailed customer information",
        icon: User,
        prompt: "get_customer_details: Show me details for a customer",
      },
      {
        id: "get_customer_statistics",
        title: "Customer Statistics",
        description: "Total customers, members, and stats",
        icon: BarChart3,
        prompt: "get_customer_statistics: Show me customer statistics",
      },
      {
        id: "create_customer",
        title: "Create Customer",
        description: "Create a new customer account",
        icon: UserPlus,
        prompt: "create_customer: I want to create a new customer",
      },
      {
        id: "update_customer",
        title: "Update Customer",
        description: "Update customer details",
        icon: PenLine,
        prompt: "update_customer: I want to update a customer's details",
      },
      {
        id: "archive_customer",
        title: "Archive Customer",
        description: "Archive a customer (soft delete)",
        icon: Folder,
        prompt: "archive_customer: I want to archive a customer",
      },
      {
        id: "activate_customer",
        title: "Activate Customer",
        description: "Activate a customer account",
        icon: UserCheck,
        prompt: "activate_customer: I want to activate a customer",
      },
      {
        id: "deactivate_customer",
        title: "Deactivate Customer",
        description: "Deactivate a customer account",
        icon: UserMinus,
        prompt: "deactivate_customer: I want to deactivate a customer",
      },
      {
        id: "reset_customer_password",
        title: "Reset Password",
        description: "Send password reset email",
        icon: RefreshCw,
        prompt: "reset_customer_password: Send password reset to a customer",
      },
    ],
  },

  // ============================================
  // BOOKING MANAGEMENT
  // ============================================
  {
    id: "booking",
    name: "Booking Management",
    icon: Calendar,
    actions: [
      {
        id: "get_bookings",
        title: "View Bookings",
        description: "Get list of bookings with filters",
        icon: Calendar,
        prompt: "get_bookings: Show me bookings",
      },
      {
        id: "check_availability",
        title: "Check Availability",
        description: "Check available time slots",
        icon: CalendarCheck,
        prompt: "check_availability: Check availability for booking",
      },
      {
        id: "create_booking",
        title: "Create Booking",
        description: "Create a new booking",
        icon: CalendarPlus,
        prompt: "create_booking: I want to create a new booking",
      },
      {
        id: "update_booking",
        title: "Update Booking",
        description: "Update booking details",
        icon: PenLine,
        prompt: "update_booking: I want to update a booking",
      },
      {
        id: "cancel_booking",
        title: "Cancel Booking",
        description: "Cancel a booking",
        icon: Trash2,
        prompt: "cancel_booking: I want to cancel a booking",
      },
      {
        id: "get_booking_details",
        title: "Booking Details",
        description: "Get detailed booking information",
        icon: FileText,
        prompt: "get_booking_details: Show me details for a booking",
      },
      {
        id: "get_booking_statistics",
        title: "Booking Statistics",
        description: "Booking stats and metrics",
        icon: BarChart3,
        prompt: "get_booking_statistics: Show me booking statistics",
      },
      {
        id: "transfer_booking",
        title: "Transfer Booking",
        description: "Transfer to another customer",
        icon: RefreshCw,
        prompt: "transfer_booking: I want to transfer a booking",
      },
      {
        id: "get_calendar_bookings",
        title: "Calendar View",
        description: "Monthly booking calendar",
        icon: CalendarClock,
        prompt: "get_calendar_bookings: Show me the booking calendar",
      },
      {
        id: "get_booking_daily_trends",
        title: "Daily Trends",
        description: "Booking trends per day",
        icon: BarChart3,
        prompt: "get_booking_daily_trends: Show me daily booking trends",
      },
    ],
  },

  // ============================================
  // MEMBERSHIP MANAGEMENT
  // ============================================
  {
    id: "membership",
    name: "Membership Management",
    icon: UserCheck,
    actions: [
      {
        id: "get_membership_plans",
        title: "View Plans",
        description: "See all membership plans",
        icon: FileText,
        prompt: "get_membership_plans: Show me all membership plans",
      },
      {
        id: "get_membership_statistics",
        title: "Membership Stats",
        description: "Active members, revenue, churn",
        icon: BarChart3,
        prompt: "get_membership_statistics: Show me membership statistics",
      },
      {
        id: "create_membership_plan",
        title: "Create Plan",
        description: "Create a new membership plan",
        icon: CalendarPlus,
        prompt: "create_membership_plan: I want to create a membership plan",
      },
      {
        id: "update_membership_plan",
        title: "Update Plan",
        description: "Update membership plan details",
        icon: PenLine,
        prompt: "update_membership_plan: I want to update a membership plan",
      },
      {
        id: "get_user_memberships",
        title: "User Memberships",
        description: "View user memberships",
        icon: Users,
        prompt: "get_user_memberships: Show me user memberships",
      },
      {
        id: "update_customer_membership",
        title: "Assign Membership",
        description: "Assign plan to customer",
        icon: UserPlus,
        prompt: "update_customer_membership: Assign membership to a customer",
      },
      {
        id: "cancel_customer_membership",
        title: "Cancel Membership",
        description: "Cancel customer's membership",
        icon: UserMinus,
        prompt: "cancel_customer_membership: Cancel a customer's membership",
      },
    ],
  },

  // ============================================
  // TRANSACTION MANAGEMENT
  // ============================================
  {
    id: "transaction",
    name: "Transactions",
    icon: CreditCard,
    actions: [
      {
        id: "get_transactions",
        title: "View Transactions",
        description: "Transaction history with filters",
        icon: Receipt,
        prompt: "get_transactions: Show me transactions",
      },
      {
        id: "get_transaction_statistics",
        title: "Transaction Stats",
        description: "Revenue, refunds, pending",
        icon: BarChart3,
        prompt: "get_transaction_statistics: Show me transaction statistics",
      },
      {
        id: "get_transaction_details",
        title: "Transaction Details",
        description: "Detailed transaction info",
        icon: FileText,
        prompt: "get_transaction_details: Show me details for a transaction",
      },
      {
        id: "process_refund",
        title: "Process Refund",
        description: "Process a refund",
        icon: DollarSign,
        prompt: "process_refund: I want to process a refund",
      },
      {
        id: "add_transaction_note",
        title: "Add Note",
        description: "Add note to transaction",
        icon: PenLine,
        prompt: "add_transaction_note: Add a note to a transaction",
      },
    ],
  },

  // ============================================
  // EVENT MANAGEMENT
  // ============================================
  {
    id: "event",
    name: "Events & Leagues",
    icon: Trophy,
    actions: [
      {
        id: "get_events",
        title: "View Events",
        description: "List events and tournaments",
        icon: Calendar,
        prompt: "get_events: Show me all events",
      },
      {
        id: "get_event_statistics",
        title: "Event Stats",
        description: "Event statistics and revenue",
        icon: BarChart3,
        prompt: "get_event_statistics: Show me event statistics",
      },
      {
        id: "create_event",
        title: "Create Event",
        description: "Schedule a new event",
        icon: CalendarPlus,
        prompt: "create_event: I want to create an event",
      },
      {
        id: "update_event",
        title: "Update Event",
        description: "Update event details",
        icon: PenLine,
        prompt: "update_event: I want to update an event",
      },
      {
        id: "cancel_event",
        title: "Cancel Event",
        description: "Cancel an event",
        icon: Trash2,
        prompt: "cancel_event: I want to cancel an event",
      },
      {
        id: "get_leagues",
        title: "View Leagues",
        description: "List all leagues",
        icon: Trophy,
        prompt: "get_leagues: Show me all leagues",
      },
      {
        id: "create_league",
        title: "Create League",
        description: "Set up a new league",
        icon: CalendarPlus,
        prompt: "create_league: I want to create a league",
      },
      {
        id: "get_league_standings",
        title: "League Standings",
        description: "View league standings",
        icon: BarChart3,
        prompt: "get_league_standings: Show me league standings",
      },
    ],
  },

  // ============================================
  // COACHING & LESSONS
  // ============================================
  {
    id: "coaching",
    name: "Coaching & Lessons",
    icon: UserCog,
    actions: [
      {
        id: "get_coaches",
        title: "View Coaches",
        description: "List all coaches",
        icon: Users,
        prompt: "get_coaches: Show me all coaches",
      },
      {
        id: "get_coach_details",
        title: "Coach Details",
        description: "Get coach information",
        icon: User,
        prompt: "get_coach_details: Show me details for a coach",
      },
      {
        id: "check_coach_availability",
        title: "Coach Availability",
        description: "Check coach schedules",
        icon: Clock,
        prompt: "check_coach_availability: Check coach availability",
      },
      {
        id: "get_coach_weekly_hours",
        title: "Weekly Hours",
        description: "Coach weekly availability",
        icon: CalendarClock,
        prompt: "get_coach_weekly_hours: Show me coach weekly hours",
      },
    ],
  },

  // ============================================
  // WAITLIST
  // ============================================
  {
    id: "waitlist",
    name: "Waitlist",
    icon: ListChecks,
    actions: [
      {
        id: "get_waitlist_entries",
        title: "View Waitlist",
        description: "See all waitlist entries",
        icon: ListChecks,
        prompt: "get_waitlist_entries: Show me the waitlist",
      },
      {
        id: "add_to_waitlist",
        title: "Add to Waitlist",
        description: "Add customer to waitlist",
        icon: UserPlus,
        prompt: "add_to_waitlist: Add a customer to the waitlist",
      },
      {
        id: "remove_from_waitlist",
        title: "Remove from Waitlist",
        description: "Remove waitlist entry",
        icon: UserMinus,
        prompt: "remove_from_waitlist: Remove from the waitlist",
      },
      {
        id: "offer_waitlist_slot",
        title: "Offer Slot",
        description: "Offer slot to waitlist customer",
        icon: Calendar,
        prompt: "offer_waitlist_slot: Offer a slot to a waitlist customer",
      },
    ],
  },

  // ============================================
  // BAY MANAGEMENT
  // ============================================
  {
    id: "bay",
    name: "Bay Management",
    icon: MapPin,
    actions: [
      {
        id: "get_bays",
        title: "View Bays",
        description: "See all bays and status",
        icon: MapPin,
        prompt: "get_bays: Show me all bays",
      },
      {
        id: "get_bay_details",
        title: "Bay Details",
        description: "Get bay information",
        icon: FileText,
        prompt: "get_bay_details: Show me details for a bay",
      },
      {
        id: "update_bay",
        title: "Update Bay",
        description: "Update bay settings",
        icon: PenLine,
        prompt: "update_bay: I want to update a bay",
      },
      {
        id: "block_bay_hours",
        title: "Block Hours",
        description: "Block bay for maintenance",
        icon: Clock,
        prompt: "block_bay_hours: Block hours for a bay",
      },
      {
        id: "unblock_bay_hours",
        title: "Unblock Hours",
        description: "Remove blocked hours",
        icon: Calendar,
        prompt: "unblock_bay_hours: Unblock hours for a bay",
      },
      {
        id: "get_bay_utilization",
        title: "Bay Utilization",
        description: "Usage metrics",
        icon: BarChart3,
        prompt: "get_bay_utilization: Show me bay utilization",
      },
    ],
  },

  // ============================================
  // ANALYTICS & DASHBOARD
  // ============================================
  {
    id: "analytics",
    name: "Analytics & Reports",
    icon: BarChart3,
    actions: [
      {
        id: "get_dashboard_analytics",
        title: "Dashboard Analytics",
        description: "KPIs, revenue, trends",
        icon: LayoutDashboard,
        prompt: "get_dashboard_analytics: Show me dashboard analytics",
      },
      {
        id: "get_revenue_trends",
        title: "Revenue Trends",
        description: "Revenue over time",
        icon: DollarSign,
        prompt: "get_revenue_trends: Show me revenue trends",
      },
      {
        id: "get_top_customers",
        title: "Top Customers",
        description: "By revenue or frequency",
        icon: Users,
        prompt: "get_top_customers: Show me top customers",
      },
      {
        id: "get_hourly_booking_heatmap",
        title: "Booking Heatmap",
        description: "Busiest hours/days",
        icon: BarChart3,
        prompt: "get_hourly_booking_heatmap: Show me booking heatmap",
      },
      {
        id: "get_customer_segmentation",
        title: "Customer Segments",
        description: "Customer behavior analysis",
        icon: Users,
        prompt: "get_customer_segmentation: Show me customer segmentation",
      },
      {
        id: "get_payment_performance",
        title: "Payment Performance",
        description: "Success rates, methods",
        icon: CreditCard,
        prompt: "get_payment_performance: Show me payment performance",
      },
      {
        id: "export_dashboard_data",
        title: "Export Data",
        description: "Export to CSV/Excel",
        icon: Download,
        prompt: "export_dashboard_data: Export dashboard data",
      },
      {
        id: "get_scheduled_reports",
        title: "Scheduled Reports",
        description: "View scheduled reports",
        icon: Calendar,
        prompt: "get_scheduled_reports: Show me scheduled reports",
      },
      {
        id: "create_scheduled_report",
        title: "Create Report",
        description: "Schedule a new report",
        icon: CalendarPlus,
        prompt: "create_scheduled_report: Create a scheduled report",
      },
    ],
  },

  // ============================================
  // PRICING & SEASONS
  // ============================================
  {
    id: "pricing",
    name: "Pricing & Seasons",
    icon: DollarSign,
    actions: [
      {
        id: "get_seasons",
        title: "View Seasons",
        description: "All pricing seasons",
        icon: Calendar,
        prompt: "get_seasons: Show me all seasons",
      },
      {
        id: "create_season",
        title: "Create Season",
        description: "Create a new season",
        icon: CalendarPlus,
        prompt: "create_season: I want to create a season",
      },
      {
        id: "get_pricing_format",
        title: "Pricing Format",
        description: "Current pricing settings",
        icon: DollarSign,
        prompt: "get_pricing_format: Show me pricing format",
      },
      {
        id: "update_pricing_format",
        title: "Update Pricing",
        description: "Update pricing settings",
        icon: PenLine,
        prompt: "update_pricing_format: Update pricing format",
      },
      {
        id: "get_operation_hours",
        title: "Operation Hours",
        description: "Hours with pricing",
        icon: Clock,
        prompt: "get_operation_hours: Show me operation hours",
      },
      {
        id: "get_business_hours",
        title: "Business Hours",
        description: "View business hours",
        icon: Clock,
        prompt: "get_business_hours: Show me business hours",
      },
      {
        id: "update_business_hours",
        title: "Update Hours",
        description: "Change business hours",
        icon: PenLine,
        prompt: "update_business_hours: Update business hours",
      },
      {
        id: "get_business_holidays",
        title: "Holidays",
        description: "Business holidays",
        icon: Calendar,
        prompt: "get_business_holidays: Show me business holidays",
      },
    ],
  },

  // ============================================
  // WEBSITE CMS
  // ============================================
  {
    id: "cms",
    name: "Website CMS",
    icon: Globe,
    actions: [
      {
        id: "get_cms_hero_content",
        title: "Hero Section",
        description: "Edit hero content",
        icon: Image,
        prompt: "get_cms_hero_content: Show me hero section",
      },
      {
        id: "update_cms_hero_content",
        title: "Update Hero",
        description: "Update hero section",
        icon: PenLine,
        prompt: "update_cms_hero_content: Update hero section",
      },
      {
        id: "get_about_us_content",
        title: "About Us",
        description: "About page content",
        icon: FileText,
        prompt: "get_about_us_content: Show me about us content",
      },
      {
        id: "get_faq_content",
        title: "FAQ",
        description: "FAQ page content",
        icon: FileText,
        prompt: "get_faq_content: Show me FAQ content",
      },
      {
        id: "get_technology_content",
        title: "Technology",
        description: "Technology page",
        icon: FileText,
        prompt: "get_technology_content: Show me technology content",
      },
      {
        id: "get_food_bar_content",
        title: "Food & Bar",
        description: "Menu content",
        icon: FileText,
        prompt: "get_food_bar_content: Show me food and bar content",
      },
      {
        id: "get_privacy_policy_content",
        title: "Privacy Policy",
        description: "Privacy policy",
        icon: FileText,
        prompt: "get_privacy_policy_content: Show me privacy policy",
      },
    ],
  },

  // ============================================
  // MARKETING POPUPS
  // ============================================
  {
    id: "popup",
    name: "Marketing Popups",
    icon: Megaphone,
    actions: [
      {
        id: "get_popup_list",
        title: "View Popups",
        description: "All marketing popups",
        icon: Megaphone,
        prompt: "get_popup_list: Show me all popups",
      },
      {
        id: "create_popup",
        title: "Create Popup",
        description: "Create new popup",
        icon: CalendarPlus,
        prompt: "create_popup: Create a marketing popup",
      },
      {
        id: "update_popup",
        title: "Update Popup",
        description: "Update popup settings",
        icon: PenLine,
        prompt: "update_popup: Update a popup",
      },
      {
        id: "get_popup_analytics",
        title: "Popup Analytics",
        description: "Performance metrics",
        icon: BarChart3,
        prompt: "get_popup_analytics: Show me popup analytics",
      },
      {
        id: "get_popup_leads",
        title: "Popup Leads",
        description: "Captured leads",
        icon: Users,
        prompt: "get_popup_leads: Show me popup leads",
      },
    ],
  },

  // ============================================
  // NOTIFICATIONS
  // ============================================
  {
    id: "notification",
    name: "Notifications",
    icon: Bell,
    actions: [
      {
        id: "get_notifications",
        title: "View Notifications",
        description: "All notifications",
        icon: Bell,
        prompt: "get_notifications: Show me notifications",
      },
      {
        id: "get_notification_settings",
        title: "Settings",
        description: "Notification settings",
        icon: Settings,
        prompt: "get_notification_settings: Show me notification settings",
      },
      {
        id: "update_notification_setting",
        title: "Update Settings",
        description: "Change notification settings",
        icon: PenLine,
        prompt: "update_notification_setting: Update notification settings",
      },
    ],
  },

  // ============================================
  // USER MANAGEMENT
  // ============================================
  {
    id: "admin",
    name: "Admin Users",
    icon: ShieldCheck,
    actions: [
      {
        id: "get_admin_users",
        title: "View Admins",
        description: "All admin users",
        icon: Users,
        prompt: "get_admin_users: Show me admin users",
      },
      {
        id: "create_admin_user",
        title: "Create Admin",
        description: "Add new admin",
        icon: UserPlus,
        prompt: "create_admin_user: Create an admin user",
      },
      {
        id: "invite_user",
        title: "Invite User",
        description: "Send admin invitation",
        icon: Mail,
        prompt: "invite_user: Invite a new admin",
      },
      {
        id: "get_pending_invitations",
        title: "Pending Invites",
        description: "View pending invites",
        icon: Hourglass,
        prompt: "get_pending_invitations: Show me pending invitations",
      },
      {
        id: "get_role_templates",
        title: "Role Templates",
        description: "Available roles",
        icon: ShieldCheck,
        prompt: "get_role_templates: Show me role templates",
      },
    ],
  },

  // ============================================
  // STAFF & PIN
  // ============================================
  {
    id: "staff",
    name: "Staff Management",
    icon: UserCog,
    actions: [
      {
        id: "get_staff_list",
        title: "View Staff",
        description: "All staff members",
        icon: Users,
        prompt: "get_staff_list: Show me staff members",
      },
      {
        id: "get_active_staff",
        title: "Active Staff",
        description: "Currently active staff",
        icon: UserCheck,
        prompt: "get_active_staff: Show me active staff",
      },
      {
        id: "create_staff_pin",
        title: "Create PIN",
        description: "Add staff PIN access",
        icon: ShieldCheck,
        prompt: "create_staff_pin: Create a staff PIN",
      },
      {
        id: "reset_staff_pin",
        title: "Reset PIN",
        description: "Reset staff PIN",
        icon: RefreshCw,
        prompt: "reset_staff_pin: Reset a staff PIN",
      },
    ],
  },

  // ============================================
  // PRODUCTS & INVENTORY
  // ============================================
  {
    id: "product",
    name: "Products & Inventory",
    icon: Package,
    actions: [
      {
        id: "get_products",
        title: "View Products",
        description: "All products",
        icon: Package,
        prompt: "get_products: Show me products",
      },
      {
        id: "create_product",
        title: "Create Product",
        description: "Add new product",
        icon: CalendarPlus,
        prompt: "create_product: Create a product",
      },
      {
        id: "update_product",
        title: "Update Product",
        description: "Update product details",
        icon: PenLine,
        prompt: "update_product: Update a product",
      },
      {
        id: "get_low_stock_products",
        title: "Low Stock",
        description: "Products running low",
        icon: Flag,
        prompt: "get_low_stock_products: Show me low stock products",
      },
      {
        id: "update_product_stock",
        title: "Update Stock",
        description: "Adjust stock quantity",
        icon: Package,
        prompt: "update_product_stock: Update product stock",
      },
    ],
  },

  // ============================================
  // MEDIA LIBRARY
  // ============================================
  {
    id: "media",
    name: "Media Library",
    icon: Image,
    actions: [
      {
        id: "get_media_files",
        title: "View Media",
        description: "Browse media files",
        icon: Image,
        prompt: "get_media_files: Show me media files",
      },
      {
        id: "get_media_storage_stats",
        title: "Storage Stats",
        description: "Storage usage",
        icon: BarChart3,
        prompt: "get_media_storage_stats: Show me storage stats",
      },
      {
        id: "delete_media_file",
        title: "Delete File",
        description: "Delete media file",
        icon: Trash2,
        prompt: "delete_media_file: Delete a media file",
      },
    ],
  },

  // ============================================
  // CONTACT FORMS
  // ============================================
  {
    id: "contact",
    name: "Contact Forms",
    icon: Mail,
    actions: [
      {
        id: "get_contact_forms",
        title: "View Contacts",
        description: "Contact submissions",
        icon: Mail,
        prompt: "get_contact_forms: Show me contact forms",
      },
      {
        id: "get_newsletter_subscribers",
        title: "Newsletter",
        description: "Newsletter subscribers",
        icon: Users,
        prompt: "get_newsletter_subscribers: Show me newsletter subscribers",
      },
      {
        id: "get_subscription_statistics",
        title: "Stats",
        description: "Subscription stats",
        icon: BarChart3,
        prompt: "get_subscription_statistics: Show me subscription stats",
      },
    ],
  },

  // ============================================
  // SETTINGS
  // ============================================
  {
    id: "settings",
    name: "Settings",
    icon: Settings,
    actions: [
      {
        id: "get_venue_settings",
        title: "Venue Settings",
        description: "Venue configuration",
        icon: Settings,
        prompt: "get_venue_settings: Show me venue settings",
      },
      {
        id: "get_branding_settings",
        title: "Branding",
        description: "Branding settings",
        icon: Image,
        prompt: "get_branding_settings: Show me branding settings",
      },
      {
        id: "update_branding_settings",
        title: "Update Branding",
        description: "Update branding",
        icon: PenLine,
        prompt: "update_branding_settings: Update branding settings",
      },
      {
        id: "get_regional_settings",
        title: "Regional",
        description: "Regional settings",
        icon: Globe,
        prompt: "get_regional_settings: Show me regional settings",
      },
      {
        id: "get_customer_booking_settings",
        title: "Booking Settings",
        description: "Customer booking settings",
        icon: Calendar,
        prompt: "get_customer_booking_settings: Show me booking settings",
      },
    ],
  },

  // ============================================
  // SCHEDULED TASKS
  // ============================================
  {
    id: "tasks",
    name: "Scheduled Tasks",
    icon: Clock,
    actions: [
      {
        id: "get_scheduled_tasks",
        title: "View Tasks",
        description: "All scheduled tasks",
        icon: ListChecks,
        prompt: "get_scheduled_tasks: Show me scheduled tasks",
      },
      {
        id: "run_task_now",
        title: "Run Task",
        description: "Manually trigger task",
        icon: Play,
        prompt: "run_task_now: Run a scheduled task now",
      },
      {
        id: "pause_scheduled_task",
        title: "Pause Task",
        description: "Pause a scheduled task",
        icon: Clock,
        prompt: "pause_scheduled_task: Pause a scheduled task",
      },
      {
        id: "get_failed_tasks",
        title: "Failed Tasks",
        description: "View failed executions",
        icon: Flag,
        prompt: "get_failed_tasks: Show me failed tasks",
      },
    ],
  },
];

/**
 * Get all actions as a flat array
 */
export const getAllActions = (): QuickAction[] => {
  return ALL_ACTION_CATEGORIES.flatMap((category) => category.actions);
};

/**
 * Get actions by category ID
 */
export const getActionsByCategory = (categoryId: string): QuickAction[] => {
  const category = ALL_ACTION_CATEGORIES.find((c) => c.id === categoryId);
  return category?.actions || [];
};

/**
 * Search actions by title or description
 */
export const searchActions = (query: string): QuickAction[] => {
  const lowerQuery = query.toLowerCase();
  return getAllActions().filter(
    (action) =>
      action.title.toLowerCase().includes(lowerQuery) ||
      action.description.toLowerCase().includes(lowerQuery)
  );
};

/**
 * Get total action count
 */
export const getTotalActionCount = (): number => {
  return getAllActions().length;
};

